/**
 * Flex Payment Block
 *
 * Injection block that loads the Flex rent payment widget SDK.
 * Site-wide: same client_id across all properties.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

const SCRIPT_ID = 'flex-payment-script';

function FlexPaymentComponent(_props: BlockProps) {
  const clientId = useBlockToken('client_id', { defaultValue: '' });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!clientId) return;

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://sdk.getflex.com/widgets/v1/widgets.js?clientId=${clientId}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [clientId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'FlexPayment',
  displayName: 'Flex Payment',
  tokens: {
    client_id: {
      label: 'Client ID',
      type: 'text',
      description: 'Flex payment SDK client ID.',
      required: true,
    },
  },
};

export const flexPaymentBlock: BlockDefinition = {
  id: 'flex-payment',
  name: 'Flex Payment',
  description: 'Flex rent payment widget allowing residents to split rent into manageable payments.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'global',
  component: FlexPaymentComponent,
  contentSchema,
  tags: ['flex', 'payment', 'rent', 'widget'],
};
